import { useLocation, useNavigate } from "react-router-dom";
import { House, Users, Lightning, Target, CaretRight, CaretDown, CalendarBlank, ChartLineUp, Wrench, Buildings, Heartbeat, Bell, ArrowsClockwise, ChartBar, GraduationCap, ChartPieSlice, Envelope, MagicWand, EnvelopeSimple, Upload, UsersThree, SignOut, PencilSimple, ChatCircleDots, ShieldCheck, Gear, X as XIcon, ClipboardText, Question } from "@phosphor-icons/react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
// FP3-053 — Persistent Super Admin org/school context selector.
// Renders above the nav for super_admin users so the platform owner
// always knows (and can switch) the operational scope.
import SuperAdminContextSelector from "@/components/layout/SuperAdminContextSelector";

const navItems = [
  { path: "/", label: "Today", icon: House },
  { path: "/caseload", label: "Caseload", icon: Users },
  // Schedule has expandable sub-nav (Iter 32) — handled inline below.
  // FP3-069 follow-up #12 — `allowedInSupervision` whitelists this
  // entry while a supervising primary is shadowing one of their
  // assistants via `?as_provider=`. Sub-nav is further restricted
  // to the Calendar tab only in supervision mode.
  { path: "/schedule", label: "Schedule", icon: CalendarBlank, hasChildren: true, allowedInSupervision: true },
  { path: "/progress", label: "Progress", icon: ChartLineUp },
  // Iter 37 — Provider Reports lives in the regular section. Gated on
  // `permissions.includes("provider")` so admins-only never see it (rare
  // but coherent).
  { path: "/reports", label: "Reports", icon: ChartPieSlice, requires: "provider" },
  // FP3-060 — IEP / MET Due Dates hub. Compliance calendar visible
  // to every authenticated provider/admin; server scopes per role.
  { path: "/due-dates", label: "Due Dates", icon: CalendarBlank, requires: "provider" },
  // FP3-035B-3 — Provider's own Service Fidelity view. Scoped to
  // their direct-service caseload by the backend.
  { path: "/reports/service-fidelity", label: "Service Fidelity", icon: ChartPieSlice, requires: "provider" },
  // FP3-035C — Reference Library (Iowa-Nebraska articulation norms,
  // visible to SLP/SLPA + admin server-side). Renders an empty-state
  // card for non-speech providers so the nav entry stays consistent.
  { path: "/references", label: "Reference Library", icon: GraduationCap, requires: "provider" },
  // FP3-055 — Supervisor Lens. Lets a primary provider see all
  // assistant-delivered evidence on students THEY supervise. Hidden
  // for assistants (they don't supervise) and admin family (they
  // have admin views).
  { path: "/supervision", label: "Supervisor Lens", icon: ShieldCheck, requires: "primary_provider" },
  // Iter 44 — Admin-to-provider messages inbox. Badge shows unread
  // count and polls every 30s (plus listens for a refresh event fired
  // by the inbox page when the user marks messages read/unread).
  // FP3-035F correction (Issue 1) — `hideInShadow: true` removes the
  // entry from the sidebar whenever admin is shadowing a provider via
  // ?as_provider=. Admins should not see (or accidentally annotate)
  // the provider's private inbox.
  //
  // FP3-069 follow-up #12 — `allowedInSupervision: true` re-allows
  // Messages while a SUPERVISING PRIMARY is shadowing one of their
  // assistants. Pete's own inbox is still scoped to him (the
  // Messages page navigates without preserving `?as_provider=`),
  // so this is purely a "stay reachable while in supervision view"
  // affordance.
  { path: "/messages", label: "Messages", icon: Envelope, badgeKey: "unread_messages", hideInShadow: true, allowedInSupervision: true },
  { path: "/notifications", label: "Notifications", icon: Bell, hideInShadow: true },
  { path: "/session", label: "Live Session", icon: Lightning },
  // FP3-061 — Provider Workboard. Scratch surface for students/tasks
  // not yet on the official caseload (referrals, screenings,
  // evaluations-in-process, watchlist). `requires: "provider"` so
  // admin-only users don't see the nav entry (they have no board in
  // the MVP). Placed at the bottom of the nav (under Live Session)
  // per user direction Jun 8, 2026.
  { path: "/workboard", label: "Workboard", icon: ClipboardText, requires: "provider" },
  // FP3-071 — Help Center MVP (Jun 8, 2026). Static accordion guide
  // (no backend / no DB). Available to every authenticated user.
  // FP3-078C — `universalAccess: true` lets admin family see this
  // entry too (otherwise the hasAdminAccess filter would hide it).
  { path: "/help", label: "Help Center", icon: Question, universalAccess: true },
  // FP3-078C — Contact / Support static page (Jun 25, 2026). Mailto
  // card pointing to admin@frameed.com. No backend, no form, no DB.
  // Universally visible (provider + admin family).
  { path: "/contact", label: "Contact", icon: EnvelopeSimple, universalAccess: true },
];

// Iter 32 — Schedule sub-nav. Builder remains the primary canvas; rules
// and health move out of the top bar so the Schedule header can stay
// focused on time navigation. Conflicts/Team intentionally absent —
// Conflicts is now surfaced via the Health pipeline; Team has been
// removed from the provider's main schedule per the new
// Provider-Calendar-vs-Shared-School-Calendar model.
const scheduleSubItems = [
  { tab: "view", label: "Calendar", icon: CalendarBlank },
  { tab: "builder", label: "Builder", icon: Wrench },
  { tab: "provider-rules", label: "Provider Rules", icon: Lightning },
  { tab: "school-rules", label: "School Rules", icon: Buildings },
  { tab: "school-year", label: "School Year", icon: ArrowsClockwise },
  { tab: "health", label: "Health", icon: Heartbeat },
];

// Iter 34 — Admin View MVP nav. Solo-first product still surfaces the
// admin lens because in solo mode it functions as "my practice
// overview." Iter 37 — Reports added; ADMIN VIEW visibility itself is
// gated on `permissions.includes("admin")`, so providers without admin
// access never see the section.
const adminItems = [
  { path: "/admin/dashboard", label: "Dashboard", icon: ChartBar },
  { path: "/admin/calendar", label: "Shared Calendar", icon: CalendarBlank },
  { path: "/admin/providers", label: "Providers", icon: Users },
  { path: "/admin/students", label: "Students", icon: GraduationCap },
  { path: "/admin/progress", label: "Progress", icon: ChartLineUp },
  { path: "/admin/coverage", label: "Coverage & Risk", icon: Heartbeat },
  // FP3-035B-3 — Org-wide Service Fidelity. Sits next to Coverage &
  // Risk because both are "is this caseload healthy?" surfaces; this
  // one focuses on minute-by-minute service delivery vs commitments.
  { path: "/admin/service-fidelity", label: "Service Fidelity", icon: ChartPieSlice },
  // Iter 41 — Reports is now a tabbed surface (Overview · Compliance ·
  // Service Delivery · Coverage · Trends). Tabs replace the previous
  // expandable sub-items so the left-nav stays clean.
  { path: "/admin/reports", label: "Reports", icon: ChartPieSlice },
  // FP3-060 — Admin entry into the same Due Dates hub. The page
  // honors the super-admin context selector + org_admin scope.
  { path: "/due-dates", label: "Due Dates", icon: CalendarBlank },
  // FP3-030A — Admin access surface. Lists pending access requests +
  // invites with send-status + one-click resend. Hidden for providers
  // by the existing hasAdminAccess gate.
  { path: "/admin/access", label: "Access & Invites", icon: EnvelopeSimple },
  // FP3-029 — Admin CSV student import. Preview + commit flow.
  { path: "/admin/import", label: "CSV Import", icon: Upload },
  // FP3-029A — Admin Student Assignment management. List org-scoped
  // students + assign / reassign / deactivate provider rows.
  { path: "/admin/assignments", label: "Student Assignments", icon: UsersThree },
  // FP3-029B — Schools & Campuses surface for admin/super_admin.
  // Pricing/billing posture lives here (super_admin only).
  { path: "/admin/schools", label: "Schools & Campuses", icon: Buildings },
  // Iter 45i — Numbers Everywhere: internal count-integrity dashboard.
  // Surfaces canonical vs surface-level counts side-by-side so admins
  // can verify the system agrees with itself after deploys.
  //
  // FP3-043 Phase A follow-up — locked down to super_admin only. The
  // page is platform-wide by design (its job is to detect count
  // drift across surfaces, not to be org-scoped). Marker is consumed
  // by the visibility filter below.
  { path: "/admin/numbers", label: "Numbers Everywhere", icon: MagicWand, superAdminOnly: true },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, ownProvider, shadowProvider, personalStatus, setPersonalStatus, preferences, provider, isSoloProvider } = useAuth();
  // FP3-075 — UX-mode + fidelity-monitoring gates with safe defaults.
  // Pre-FP3-075 users (no field on their doc) resolve to advanced/on
  // on the server, so the sidebar still renders exactly as it did
  // before for everyone today.
  const isSimpleMode = preferences?.ui_mode === "simple";
  const isFidelityOff = preferences?.fidelity_monitoring === "off";
  // Iter 33 — Schedule sub-nav can now be explicitly collapsed even
  // while the user is on /schedule. Defaults to expanded when entering
  // the Schedule area.
  const [scheduleExpanded, setScheduleExpanded] = useState(
    location.pathname.startsWith("/schedule")
  );

  // FP3-029A — Detect solo provider so we can surface the
  // "Import My Caseload" nav item. FP3-119 — Consolidated onto the
  // AuthProvider `isSoloProvider` context value; previously the
  // sidebar re-called /auth/me in its OWN useEffect to derive the
  // same flag, doubling the /auth/me traffic on every route change.
  const isSolo = !!isSoloProvider;
  // Iter 44 — Poll unread-message count so the sidebar badge stays
  // honest. Cheap GET returning {count, provider_id}; polls every 30s
  // AND re-fetches immediately when the Messages page broadcasts a
  // "messages:refresh-badge" event (after mark-read/unread).
  // FP3-069 follow-up #13 — Badge now sums admin notes
  // (`/messages/unread-count?provider_id=`) AND supervisor DMs
  // (`/provider-messages/unread-count`) so supervisor→assistant
  // messages no longer hide from the recipient's sidebar count.
  const [unreadMessages, setUnreadMessages] = useState(0);
  useEffect(() => {
    if (!provider?.id) return;
    let cancelled = false;
    const tick = () => {
      Promise.all([
        api.getMessageUnreadCount(provider.id).catch(() => ({ data: { count: 0 } })),
        api.getProviderMessageUnreadCount().catch(() => ({ data: { count: 0 } })),
      ]).then(([adminR, dmR]) => {
        if (cancelled) return;
        const adminN = adminR.data?.count || 0;
        const dmN = dmR.data?.count || 0;
        setUnreadMessages(adminN + dmN);
      });
    };
    tick();
    const iv = setInterval(tick, 30_000);
    const onRefresh = () => tick();
    window.addEventListener("messages:refresh-badge", onRefresh);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("messages:refresh-badge", onRefresh);
    };
  }, [provider?.id]);
  const badgeValues = { unread_messages: unreadMessages };

  // Auto-expand whenever the user enters /schedule, but don't force
  // collapse when leaving — leaving simply hides the sub-nav anyway.
  useEffect(() => {
    if (location.pathname.startsWith("/schedule")) setScheduleExpanded(true);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Iter 32 — Read the active Schedule sub-tab from the URL so the
  // sidebar highlight stays in sync with the page state.
  const search = new URLSearchParams(location.search);
  const activeScheduleTab = search.get("tab") || "view";
  const onSchedule = location.pathname.startsWith("/schedule");
  const showScheduleSubnav = onSchedule && scheduleExpanded;

  // Iter 37 — Permission-gated visibility. Defaults to `["provider"]`
  // for safety on records that haven't been migrated yet.
  // FP3-028 — Admin nav visibility is now driven by the *logged-in
  // user's* permission_role, not the seeded current-provider's
  // legacy `permissions` array. This way Sarah (provider) never sees
  // admin nav links even if her demo provider record carries
  // `permissions: ["provider", "admin"]` for backward compatibility
  // with Iter 37's solo-mode admin view.
  const permissions = provider?.permissions || ["provider"];
  const hasProviderAccess = permissions.includes("provider");
  const hasAdminAccess = ["admin", "org_admin", "super_admin"].includes(user?.permission_role || "");
  // FP3-029B3 — `as_provider` URL flag enables a read-only provider
  // workspace inspection mode for admin family users. When the flag
  // is absent, admin/org_admin/super_admin should default to the
  // admin nav only; provider-workspace links (Today / Caseload /
  // Schedule / Progress / Reports / Messages / Notifications / Live
  // Session) stay hidden. When the flag is present, the provider
  // nav re-appears AND a banner renders explaining the read-only mode.
  const asProviderId = (() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("as_provider") || "";
  })();
  // FP3-069 follow-up #12 — Supervision-shadow flag for non-admin
  // callers. When a supervising primary clicks "Schedule" on their
  // assistant chip (AssistantsCard), the URL carries `?as_provider=`
  // and we want the SAME shadow-nav protection that admins get —
  // every sidebar click must preserve the param, the nav must be
  // restricted to the allowed supervision surfaces, and the page
  // chrome must show the read-only banner.
  //
  // IMPORTANT: compare `asProviderId` against the CALLER's OWN
  // provider id (`ownProvider` from useAuth), NOT the locally-fetched
  // `provider` state — because `api.getCurrentProvider()` returns
  // the SHADOW target's record when `?as_provider=` is set (that's
  // exactly the fix this follow-up landed). Using the local
  // `provider.id` here would make `isOwnPid` true for every shadow
  // run and silently disable the supervision restrictions.
  const isOwnPid =
    !!ownProvider?.id && !!asProviderId && asProviderId === ownProvider.id;
  const inSupervisionView = !!asProviderId && !hasAdminAccess && !isOwnPid;
  const inProviderView = (!!asProviderId && hasAdminAccess) || inSupervisionView;

  // FP3-037 — Super admin drill-in flag. When super_admin opens
  // `?as_org=` on any admin surface, every subsequent sidebar click
  // must preserve the param so the admin nav stays scoped to the
  // chosen org. The banner above the page renders "Super Admin View
  // — Viewing {Org}" while this flag is on.
  const asOrgId = (() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("as_org") || "";
  })();
  // FP3-053 — Optional school-level context. Sidebar must propagate
  // it alongside `as_org` so every admin route stays pinned to the
  // selected campus.
  const asSchoolId = (() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("as_school") || "";
  })();
  const inPlatformOrgView =
    !!asOrgId && user?.permission_role === "super_admin";

  // FP3-035F Issue 2 — Read-Only Provider Workspace flow stability.
  //
  // When admin is shadowing a provider via `?as_provider=`, EVERY
  // subsequent navigation from the sidebar must preserve the query
  // param. Without this helper, clicking Caseload/Schedule/Reports/etc
  // strips the param and the next route silently flips back to admin
  // mode, which is the bug the user reported.
  //
  // FP3-037 — Same protection applies for `?as_org=` (super admin
  // drilled into a specific organization).
  //
  // The helper also handles paths that already carry their own
  // query string (e.g. `/schedule?tab=builder` from the sub-nav).
  const navigateWithShadow = (path) => {
    if (!inProviderView && !inPlatformOrgView) {
      navigate(path);
      return;
    }
    const [pathOnly, existingQs = ""] = path.split("?");
    const qs = new URLSearchParams(existingQs);
    // FP3-069 follow-up #12 — Messages is `allowedInSupervision` so
    // the supervisor can DM the assistant while still in supervision
    // mode — but the supervisor's inbox is HER OWN, not the
    // assistant's. Drop the `as_provider` param on /messages so the
    // page hits the supervisor's normal inbox (and the FP3-035F
    // admin-redirect guard never fires for the supervision flow).
    const isMessagesNav = pathOnly === "/messages";
    if (inProviderView && !(inSupervisionView && isMessagesNav)) {
      qs.set("as_provider", asProviderId);
    }
    if (inPlatformOrgView) qs.set("as_org", asOrgId);
    // FP3-053 — Preserve school context too. Without this the user
    // would lose their pinned campus the moment they clicked any
    // other admin nav item.
    if (inPlatformOrgView && asSchoolId) qs.set("as_school", asSchoolId);
    const qsStr = qs.toString();
    navigate(qsStr ? `${pathOnly}?${qsStr}` : pathOnly);
  };

  const exitProviderView = () => {
    // FP3-069 follow-up #12 — Supervising primary exits back to
    // their own Caseload (where the AssistantsCard lives). Admin
    // family still exits to their dashboard.
    if (inSupervisionView) {
      navigate("/caseload");
      return;
    }
    // Clear the shadow flag entirely + send admin home to the admin
    // dashboard. Drops any unrelated query params on the current URL
    // so a stale `?tab=` or similar doesn't tag along.
    navigate("/admin/dashboard");
  };

  const visibleNavItems = navItems.filter((it) => {
    // FP3-029B5 — Admin family default view hides ALL provider-
    // workspace items, including the legacy "Reports" entry that
    // carries `requires: "provider"`. This must run BEFORE the
    // requires-check so the early-return on `hasProviderAccess`
    // (which defaults to true on every record carrying the
    // provider permission) doesn't leak Reports back into the
    // admin sidebar. When admin enters `?as_provider=` to inspect
    // a provider's workspace, the provider nav reappears.
    //
    // FP3-078C — `universalAccess: true` entries (Help Center,
    // Contact) bypass this filter so admin family can still reach
    // help + support surfaces from their own sidebar.
    if (hasAdminAccess && !inProviderView && !it.universalAccess) return false;
    // FP3-069 follow-up #12 — Non-admin supervision shadow.
    // Restrict the visible nav to entries explicitly flagged
    // `allowedInSupervision: true` (Schedule + Messages today).
    // Every other entry is hidden so a stray click can't silently
    // surface the supervisor's own data under the "Read-only —
    // Max" banner.
    if (inSupervisionView && !it.allowedInSupervision) return false;
    // FP3-035F correction (Issue 1) — Hide Messages + Notifications
    // (the provider's private inboxes) when admin is shadowing.
    // FP3-069 follow-up #12 — Continue to honor `hideInShadow` for
    // ADMIN shadow only. In supervision-shadow we explicitly want
    // Messages reachable (allowedInSupervision wins above) so the
    // supervisor can DM the assistant without leaving the view.
    if (inProviderView && !inSupervisionView && it.hideInShadow) return false;
    // FP3-055 — Supervisor Lens is only meaningful for primary
    // providers (assistants don't supervise; admin family already
    // has scoped admin views). Gate strictly on permission_role
    // AND legacy provider_role_type so SLPAs/COTAs/PTAs in the
    // existing preview seed (where permission_role still reads
    // "provider" but provider_role_type="assistant") stay out.
    if (it.requires === "primary_provider") {
      const pr = (user?.permission_role || "").toLowerCase();
      if (pr !== "provider") return false;
      const isLegacyAssistant = (user?.provider_role_type || "").toLowerCase() === "assistant";
      return !isLegacyAssistant;
    }
    // FP3-075 bugfix — Move the fidelity gate ABOVE the `requires`
    // check so it actually runs for items carrying
    // `requires: "provider"`. The Service Fidelity entry is a
    // provider-required item; the previous order returned
    // `hasProviderAccess` at the next line and short-circuited the
    // fidelity gate, leaving the entry visible even after a solo
    // provider flipped Fidelity Monitoring to Off.
    //
    // The route (/reports/service-fidelity) stays mounted in the
    // router so direct links + bookmarks keep working; we only
    // declutter the sidebar.
    if (isFidelityOff && it.path === "/reports/service-fidelity") return false;
    if (it.requires === "provider") return hasProviderAccess;
    if (it.requires === "admin") return hasAdminAccess;
    return true;
  });

  return (
    <aside
      data-testid="sidebar"
      className="w-64 bg-[#F9F8F6] border-r border-[#E5E3DB] flex flex-col h-screen sticky top-0 shrink-0"
    >
      {/* Brand */}
      <div className="px-6 py-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#1E3F33] flex items-center justify-center">
            <span className="text-white text-sm font-bold heading-font">F</span>
          </div>
          <div>
            <span className="text-[15px] font-bold text-[#1E3F33] heading-font tracking-tight">Frame</span>
            <span className="text-[15px] font-medium text-[#8FA998] heading-font tracking-tight ml-0.5">Practice</span>
          </div>
        </div>
      </div>

      <Separator className="bg-[#E5E3DB]" />

      {/* FP3-053 — Persistent Super Admin context selector. Renders
          for super_admin users only (the component returns null
          otherwise) so providers/org admins see no extra chrome.
          Sits ABOVE the nav so it reads like a global scope picker
          rather than just another menu item. */}
      <SuperAdminContextSelector />

      {/* FP3-069 follow-up #12 — Supervision shadow header. Renders
          ABOVE the nav whenever a supervising primary is shadowing
          one of their assistants. Clear "you are in read-only view"
          cue + a one-click Exit so the user never has to wonder
          which workspace the sidebar is operating in. The banner
          on the page chrome carries the same Exit + the more
          detailed assistant-name copy; this is the persistent
          sidebar counterpart. */}
      {inSupervisionView && (
        <div
          data-testid="sidebar-supervision-header"
          className="mx-3 mt-3 mb-2 rounded-lg bg-[#FDF3D8] border border-[#C8951F]/40 px-3 py-2"
        >
          <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#9C6B2E] mb-0.5">
            Supervision · Read-only
          </p>
          <p
            data-testid="sidebar-supervision-subline"
            className="text-[11px] text-[#5A4A1A] leading-snug"
          >
            Pages outside this list are paused until you exit.
          </p>
          <button
            type="button"
            data-testid="sidebar-supervision-exit"
            onClick={exitProviderView}
            className="mt-2 w-full text-[11.5px] font-semibold text-white bg-[#9C6B2E] hover:bg-[#7A521F] rounded-md py-1.5 transition-colors"
          >
            Exit read-only
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {visibleNavItems.map(({ path, label, icon: Icon, hasChildren, badgeKey }) => {
          const badgeVal = badgeKey ? badgeValues[badgeKey] || 0 : 0;
          return (
          <div key={path}>
            <button
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => {
                // Iter 33 — Re-clicking the Schedule row while already
                // on /schedule toggles the sub-nav instead of navigating.
                if (hasChildren && onSchedule) {
                  setScheduleExpanded((v) => !v);
                  return;
                }
                navigateWithShadow(path);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive(path)
                  ? "bg-[#E8F0EB] text-[#1E3F33]"
                  : "text-[#6B7280] hover:bg-[#F3F1EC] hover:text-[#1F2937]"
              }`}
            >
              <Icon size={20} weight={isActive(path) ? "duotone" : "regular"} />
              <span>{label}</span>
              {badgeVal > 0 && (
                <span
                  data-testid={`nav-${label.toLowerCase()}-badge`}
                  className="ml-auto bg-[#1E3F33] text-white text-[10px] font-semibold rounded-full min-w-[20px] h-[18px] flex items-center justify-center px-1.5 tabular-nums"
                >
                  {badgeVal > 99 ? "99+" : badgeVal}
                </span>
              )}
              {hasChildren ? (
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`nav-${label.toLowerCase()}-toggle`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!onSchedule) {
                      navigateWithShadow(path);
                      return;
                    }
                    setScheduleExpanded((v) => !v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!onSchedule) navigateWithShadow(path);
                      else setScheduleExpanded((v) => !v);
                    }
                  }}
                  className="ml-auto cursor-pointer p-0.5 -m-0.5 rounded hover:bg-[#E5E3DB]/60"
                >
                  <CaretDown
                    size={14}
                    className={`opacity-60 transition-transform ${
                      showScheduleSubnav ? "" : "-rotate-90"
                    }`}
                  />
                </span>
              ) : (
                isActive(path) && <CaretRight size={14} className="ml-auto opacity-50" />
              )}
            </button>
            {/* Schedule sub-nav. Visible only while the user is inside
                /schedule AND has not collapsed it via the carret toggle.
                FP3-069 follow-up #12 — In supervision shadow we restrict
                the sub-nav to the Calendar tab only. Builder / Provider
                Rules / School Rules / School Year / Health are NOT
                supervision-aware (they mutate Pete's own data when the
                page loads), so we hide them entirely.
                FP3-075C — In Simple Mode the Schedule sub-nav is
                strictly Calendar + Builder. The "Show advanced tools"
                disclosure that used to live below the sub-nav was
                removed per product direction: advanced surfaces
                should only appear when the user explicitly flips
                Interface Mode to Advanced from Account → Workspace
                preferences. The FP3-075B "Edit schedule dates" CTA
                still works because it navigates directly to
                "/schedule?tab=school-year"; the page loads even when
                the sidebar doesn't list it. */}
            {hasChildren && showScheduleSubnav && (
              <div className="ml-3 mt-1 mb-2 space-y-0.5 border-l border-[#E5E3DB] pl-2">
                {scheduleSubItems
                  .filter((sub) => !inSupervisionView || sub.tab === "view")
                  .filter((sub) => {
                    if (!isSimpleMode) return true;
                    // Simple Mode: ONLY Calendar + Builder, ever.
                    return sub.tab === "view" || sub.tab === "builder";
                  })
                  .map((sub) => {
                  const SubIcon = sub.icon;
                  const subActive = activeScheduleTab === sub.tab;
                  return (
                    <button
                      key={sub.tab}
                      data-testid={`subnav-${sub.tab}`}
                      onClick={() => navigateWithShadow(`/schedule?tab=${sub.tab}`)}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
                        subActive
                          ? "bg-white text-[#1E3F33] shadow-sm"
                          : "text-[#6B7280] hover:bg-[#F3F1EC] hover:text-[#1F2937]"
                      }`}
                    >
                      <SubIcon size={14} weight={subActive ? "duotone" : "regular"} />
                      <span>{sub.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          );
        })}

        {/* FP3-029A — Solo provider "Import My Caseload" entry.
            Sits BELOW the regular provider nav and ABOVE the admin
            section, only renders for provider users with
            account_type=individual_provider. School-team providers
            never see this. */}
        {isSolo && (
          <div className="mt-4 pt-3 border-t border-[#E5E3DB]">
            <p className="px-3 mb-1.5 text-[9px] uppercase tracking-[0.12em] font-semibold text-[#6B7280]">
              My Workspace
            </p>
            <button
              data-testid="nav-import-my-caseload"
              onClick={() => navigate("/import")}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                location.pathname === "/import"
                  ? "bg-[#E8F0EB] text-[#1E3F33]"
                  : "text-[#6B7280] hover:bg-[#F3F1EC] hover:text-[#1F2937]"
              }`}
            >
              <Upload size={20} weight={location.pathname === "/import" ? "duotone" : "regular"} />
              <span>Import My Caseload</span>
            </button>
          </div>
        )}

        {/* Iter 34 — Admin View MVP section. Visually separated as the
            "coordination/oversight lens" — slate accent, distinct from
            provider-green nav items. Iter 37 — gated on
            `permissions.includes("admin")`. FP3-035F Issue 2 — Hidden
            while admin is shadowing a provider; the explicit "Exit to
            Admin" banner above is the one supported way back. This
            prevents an accidental click on "Service Fidelity" (which
            exists in BOTH the provider + admin sections) from silently
            stripping ?as_provider= and dropping the admin back into
            admin mode mid-walkthrough.

            FP3-053 — Section is now split into two labeled groups
            for super_admin operators:
              • PLATFORM — control-center surfaces (org/billing setup,
                pilot wizard, platform-wide diagnostics).
              • ADMIN VIEW — operational pages that respect the
                currently-selected org/school context.
            Org admins / admins see only the "Admin View" header
            because they have no platform-level entries to begin with. */}
        {hasAdminAccess && !inProviderView && (
          <div className="mt-6 pt-3 border-t border-[#E5E3DB]">
            {/* FP3-037 + FP3-053 — Platform section. Super-admin only.
                A labeled header groups platform-level surfaces so
                the operator can tell at a glance which clicks are
                global vs scoped to the active org/school. */}
            {user?.permission_role === "super_admin" && (
              <div className="mb-3">
                <p
                  data-testid="sidebar-section-platform"
                  className="px-3 mb-1.5 text-[9px] uppercase tracking-[0.12em] font-semibold text-[#1F4F73]"
                >
                  Platform
                </p>
                <button
                  data-testid="admin-nav-platform-dashboard"
                  onClick={() => navigate("/platform/dashboard")}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                    location.pathname.startsWith("/platform/dashboard") ||
                    location.pathname.startsWith("/platform/org/")
                      ? "bg-[#1F4F73]/10 text-[#1F4F73]"
                      : "text-[#1F4F73] hover:bg-[#E6F1F8] hover:text-[#0F3554]"
                  }`}
                >
                  <ShieldCheck
                    size={16}
                    weight={
                      location.pathname.startsWith("/platform/dashboard") ||
                      location.pathname.startsWith("/platform/org/")
                        ? "duotone"
                        : "regular"
                    }
                  />
                  <span>Platform Dashboard</span>
                  {(location.pathname.startsWith("/platform/dashboard") ||
                    location.pathname.startsWith("/platform/org/")) && (
                    <CaretRight size={12} className="ml-auto opacity-50" />
                  )}
                </button>
                <button
                  data-testid="admin-nav-pilot-setup"
                  onClick={() => navigate("/platform/pilot-setup")}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                    location.pathname.startsWith("/platform/pilot-setup")
                      ? "bg-[#1F4F73]/10 text-[#1F4F73]"
                      : "text-[#1F4F73] hover:bg-[#E6F1F8] hover:text-[#0F3554]"
                  }`}
                >
                  <MagicWand
                    size={16}
                    weight={
                      location.pathname.startsWith("/platform/pilot-setup")
                        ? "duotone"
                        : "regular"
                    }
                  />
                  <span>Pilot Setup</span>
                  {location.pathname.startsWith("/platform/pilot-setup") && (
                    <CaretRight size={12} className="ml-auto opacity-50" />
                  )}
                </button>
              </div>
            )}
            {/* FP3-074 — Admin View visibility rule.
                For super_admin: only render the bulky Admin View list
                  when (a) drilled into a specific org via `?as_org=`
                  OR (b) already inside an `/admin/...` route. In
                  pure "platform overview" mode we strip it so the
                  sidebar stays focused on platform-level work.
                For admin / org_admin: ALWAYS render (their default
                  navigation is the Admin View list). */}
            {(user?.permission_role !== "super_admin" ||
              inPlatformOrgView ||
              location.pathname.startsWith("/admin/")) && (
              <>
                <p
                  data-testid="sidebar-section-admin-view"
                  className="px-3 mb-1.5 text-[9px] uppercase tracking-[0.12em] font-semibold text-[#6B7280]"
                >
                  Admin View
                </p>
                {adminItems
                  // FP3-043 Phase A follow-up — superAdminOnly items are
                  // hidden from org_admin / admin. Numbers Everywhere is
                  // the first such item; future platform diagnostics
                  // should mark themselves the same way.
                  .filter((it) => !it.superAdminOnly || user?.permission_role === "super_admin")
                  // FP3-119 — Fidelity-Off gating on Admin View. When
                  // the viewer preference `fidelity_monitoring="off"`,
                  // hide the Service Fidelity nav entry entirely
                  // (not merely visually gray). The route stays
                  // mounted so a direct/bookmarked URL is handled by
                  // the page-level disabled state, not by 404-ing.
                  .filter((it) => !(isFidelityOff && it.path === "/admin/service-fidelity"))
                  .map(({ path, label, icon: Icon }) => {
                  const active = location.pathname === path || location.pathname.startsWith(path + "/");
                  return (
                    <button
                      key={path}
                      data-testid={`admin-nav-${label.toLowerCase().replace(/\s|&/g, "-").replace(/--+/g, "-")}`}
                      onClick={() => navigateWithShadow(path)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                        active
                          ? "bg-[#3F4756]/10 text-[#3F4756]"
                          : "text-[#6B7280] hover:bg-[#F3F1EC] hover:text-[#1F2937]"
                      }`}
                    >
                      <Icon size={16} weight={active ? "duotone" : "regular"} />
                      <span>{label}</span>
                      {active && <CaretRight size={12} className="ml-auto opacity-50" />}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </nav>

      <Separator className="bg-[#E5E3DB]" />

      {/* User Identity — FP3-028. Surfaces the LOGGED-IN user
          (sidebar identity follows auth, not the seeded current
          provider). When admin enters read-only mode via
          `?as_provider=`, the calm "Read-only · <Provider>" chip
          uses the shadow provider record (FP3-029B4) so the displayed
          name always matches the URL selection — no more fallback to
          the seeded `is_current=True` provider (Sarah). */}
      {user && (
        <div className="px-4 py-4" data-testid="sidebar-user-identity">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9 border border-[#E5E3DB]">
              <AvatarFallback className="bg-[#E8F0EB] text-[#1E3F33] text-xs font-semibold heading-font">
                {(user.first_name?.[0] || user.email?.[0] || "?").toUpperCase()}
                {(user.last_name?.[0] || "").toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p
                data-testid="sidebar-user-name"
                className="text-sm font-medium text-[#1F2937] truncate heading-font"
              >
                {user.first_name || user.last_name
                  ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                  : user.email}
              </p>
              <p className="text-xs text-[#6B7280] truncate capitalize">
                {(user.permission_role || "provider").replace("_", " ")}
              </p>
              {/* FP3-029B4 — Read-only chip is keyed off the shadow
                  provider (loaded from /admin/providers/{id} matching
                  the URL `?as_provider=` param), NOT the seeded
                  `is_current=True` provider. */}
              {hasAdminAccess && inProviderView && shadowProvider && shadowProvider.id && (
                <p
                  data-testid="sidebar-viewing-as"
                  className="text-[10px] text-[#7A6A2C] truncate mt-0.5 font-medium"
                >
                  Read-only · {shadowProvider.first_name} {shadowProvider.last_name}
                </p>
              )}
              {/* FP3-029B4 — Personal/team-facing status, settable by
                  the signed-in user (provider or admin). Distinct
                  from administrative provider_status. */}
              <PersonalStatusInline
                value={personalStatus || ""}
                onChange={setPersonalStatus}
              />
            </div>
            <WorkspaceInfoButton />
            <AccountSettingsButton />
            <LogoutButton />
          </div>
        </div>
      )}
    </aside>
  );
}

// FP3-042 — Gear icon next to Sign Out. Routes the signed-in user to
// /account/security where they can change their name/email/password.
function AccountSettingsButton() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      data-testid="sidebar-account-settings-btn"
      title="Account settings"
      onClick={() => navigate("/account/security")}
      className="p-1.5 rounded-md text-[#6B7280] hover:text-[#1E3F33] hover:bg-[#F1F2F4] transition-colors"
    >
      <Gear size={16} />
    </button>
  );
}

// FP3-049 — Workspace info icon. Routes the signed-in user to
// /account/workspace where they can see their organization,
// campuses, provider type, role, supervising provider, and the
// admin contacts for their organization.
function WorkspaceInfoButton() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      data-testid="sidebar-workspace-info-btn"
      title="Workspace info"
      onClick={() => navigate("/account/workspace")}
      className="p-1.5 rounded-md text-[#6B7280] hover:text-[#1E3F33] hover:bg-[#F1F2F4] transition-colors"
    >
      <Buildings size={16} />
    </button>
  );
}

// FP3-028 — Inline logout. Kept in this file to avoid a new component
// just for one icon button. Calls /auth/logout, then sends the user to
// /login on success.
function LogoutButton() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      data-testid="sidebar-logout-btn"
      title="Sign out"
      onClick={async () => {
        await logout();
        navigate("/login", { replace: true });
      }}
      className="p-1.5 rounded-md text-[#6B7280] hover:text-[#1E3F33] hover:bg-[#F1F2F4] transition-colors"
    >
      <SignOut size={16} />
    </button>
  );
}

// FP3-029B4 — Inline editor for the optional, team-facing
// personal_status string. Distinct from administrative
// provider_status. Set by the signed-in user; visible to teammates
// and admins on Provider Detail. Cap = 80 chars (enforced
// server-side too).
function PersonalStatusInline({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const save = async () => {
    const trimmed = (draft || "").trim();
    try {
      const r = await api.updateMyPersonalStatus(trimmed);
      onChange?.(r.data?.personal_status || "");
      toast.success(trimmed ? "Status updated" : "Status cleared");
      setEditing(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not update status"
      );
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        data-testid="sidebar-personal-status-display"
        onClick={() => setEditing(true)}
        className="group mt-1 flex items-center gap-1 text-[10.5px] text-[#6B7280] hover:text-[#1F2937] truncate max-w-full"
        title={value ? "Edit your status" : "Set a status visible to teammates"}
      >
        <ChatCircleDots size={11} weight={value ? "duotone" : "regular"} className="text-[#8FA998] shrink-0" />
        <span className="truncate">
          {value || (
            <span className="italic text-[#9AA0A6]">Set status…</span>
          )}
        </span>
        <PencilSimple
          size={9}
          className="opacity-0 group-hover:opacity-60 transition-opacity shrink-0"
        />
      </button>
    );
  }

  return (
    <div className="mt-1 flex items-center gap-1 max-w-full">
      <input
        data-testid="sidebar-personal-status-input"
        type="text"
        maxLength={80}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            save();
          } else if (e.key === "Escape") {
            setDraft(value || "");
            setEditing(false);
          }
        }}
        autoFocus
        placeholder="At Lincoln today…"
        className="flex-1 min-w-0 h-6 text-[10.5px] px-1.5 border border-[#E5E3DB] rounded bg-white focus:outline-none focus:border-[#1E3F33]"
      />
      <button
        data-testid="sidebar-personal-status-save"
        onClick={save}
        className="h-6 px-1.5 text-[10px] rounded bg-[#1E3F33] text-white hover:bg-[#173127]"
      >
        Save
      </button>
      <button
        data-testid="sidebar-personal-status-cancel"
        onClick={() => {
          setDraft(value || "");
          setEditing(false);
        }}
        className="h-6 w-6 flex items-center justify-center rounded text-[#6B7280] hover:bg-[#F1F2F4]"
        title="Cancel"
      >
        <XIcon size={10} />
      </button>
    </div>
  );
}
